import moment from 'moment';
import {useUserStore} from '../stores/userStore';
import {useAccountStore} from '../stores/accountStore';
import {useSecurityStore} from '../stores/securityStore';
import {useGlobalStore} from '../stores/globalStore';
import * as Sentry from '@sentry/vue';
import {postMessage} from './helpers/messageHelper';

const requestMap = new Map<string, moment.Moment[]>();

const requestThreshold = {
    seconds: 10,
    count: 20,
};

function getAdditionalErrorInformation(): Record<string, unknown> {
    try {
        const {user} = useUserStore();
        const {account} = useAccountStore();
        const {authType} = useSecurityStore();

        return {userId: user?.id, accountId: account?.id, authType};
    } catch (error) {
        return {caughtError: error};
    }
}

function clearOldEntries() {
    const deletionThresholdDate = moment().subtract({second: 30});

    for (const key of requestMap.keys()) {
        const urlRequests = requestMap.get(key)!;
        const currentRequests = urlRequests.filter(timestamp =>
            timestamp.isAfter(deletionThresholdDate)
        );
        if (!currentRequests.length) {
            requestMap.delete(key);
        } else if (currentRequests.length !== urlRequests.length) {
            requestMap.set(key, currentRequests);
        }
    }
}

export const requestLoopDetector = {
    isActive: true,
    check(url: string): boolean {
        if (!this.isActive) {
            return true;
        }
        if (!requestMap.has(url)) {
            requestMap.set(url, []);
        }

        const urlRequests = requestMap.get(url)!;
        urlRequests.push(moment());
        clearOldEntries();

        const thresholdDate = moment().subtract({second: requestThreshold.seconds});
        const requestsAfterThreshold = urlRequests.filter(timestamp =>
            timestamp.isAfter(thresholdDate)
        );

        if (requestsAfterThreshold.length >= requestThreshold.count) {
            const sentryErrorObject = getAdditionalErrorInformation();

            sentryErrorObject.previousRequestUrls = Array.from(requestMap)
                .map(([name, timestamps]) =>
                    timestamps.map(timestamp => `${timestamp.format('HH:mm:ss:SSS')} | ${name}`)
                )
                .flat()
                .sort();

            try {
                useGlobalStore().resetWithExceptions();
            } catch {
                sentryErrorObject.resetWithExceptionFailed = true;
                // resetWithExceptions failed — directly reset security store so that
                // the MSAL flag is cleared before reload. Without this, the app
                // restarts with msal=true in secure storage but no MSAL accounts in
                // localStorage, causing an immediate failed re-login attempt.
                try {
                    useSecurityStore().reset();
                } catch {
                    // best-effort; the reload below will enforce a clean state
                }
            }

            sentryErrorObject.message = 'checkRequestLoop';
            Sentry.captureException(sentryErrorObject);

            localStorage.clear();
            postMessage('desklyTokenExpired');
            window.location.reload();

            return false;
        }

        return true;
    },
};
